<!--
  @component
  @name BirthdateInput
  @description
    A component that allows users to enter a birthdate using separate month, day, and year inputs.

  @example
    <BirthdateInput bind:birthdate={someDateValue} />

  @events
    change: Fired when the user changes any of the date inputs and a valid date is created.
-->

<script lang="ts">
import { Birthdate } from '../birthday';
import { ALL_MONTHS_FULL } from '../constants';

// Props
export let birthdate: Birthdate = null;
export let inputId = 'birthdate';
export let autoFocus = false;
export let isValid = false;

export let onchange: ((event: { birthdate: Birthdate }) => void) | undefined =
  undefined;

let dayInputEl;
let yearInputEl;

function parseNumberString(input: string): number {
  const parsed = parseInt(input, 10);
  if (Number.isNaN(parsed)) return 0;
  return parsed;
}

function parseDateInputValue(
  day: string,
  month: string,
  year: string
): { date: Date | null; errors: string[] } {
  const errors = [];
  let birthdateDay = parseNumberString(day);
  let birthdateMonth = parseNumberString(month);
  let birthdateYear = parseNumberString(year);

  // Only validate fields that have content
  // Validate month (1-12) - only if month field has content
  if (month && (birthdateMonth < 1 || birthdateMonth > 12)) {
    errors.push('Month must be between 1 and 12');
  }

  // Validate year (not in future, not more than 125 years ago) - only if year field has content
  if (year) {
    const currentYear = new Date().getFullYear();
    const minYear = currentYear - 125;
    if (birthdateYear > currentYear) {
      errors.push('Year cannot be in the future');
    } else if (birthdateYear < minYear) {
      errors.push(`Year cannot be more than 125 years ago (${minYear})`);
    }
  }

  // Validate day (basic range first) - only if day field has content
  if (day && (birthdateDay < 1 || birthdateDay > 31)) {
    errors.push('Day must be between 1 and 31');
  }

  // Only do advanced validation if all fields have content and basic validation passed
  if (month && day && year && errors.length === 0) {
    const testDate = new Date(birthdateYear, birthdateMonth - 1, birthdateDay);

    // Check if the date rolled over (invalid day for month)
    if (
      testDate.getFullYear() !== birthdateYear ||
      testDate.getMonth() !== birthdateMonth - 1 ||
      testDate.getDate() !== birthdateDay
    ) {
      errors.push(
        `Invalid day for ${ALL_MONTHS_FULL[birthdateMonth - 1]} ${birthdateYear}`
      );
    } else {
      // Check if date is not in the future
      if (testDate > new Date()) {
        errors.push('Date cannot be in the future');
      }

      return {
        date: new Date(
          Date.UTC(birthdateYear, birthdateMonth - 1, birthdateDay)
        ),
        errors: [],
      };
    }
  }

  return { date: null, errors };
}

// Svelte binds the value of the input to a string, so we need to parse it
// and store it in a Date object.
let parsedDate: Date;
let birthdateDayStr = '';
let birthdateMonthStr = '';
let birthdateYearStr = '';
let validationErrors: string[] = [];

// One-way initializing of string values from birthdate prop
function initializeFromBirthdate() {
  if (birthdate) {
    birthdateMonthStr = (birthdate.layBirthMonth() + 1)
      .toString()
      .padStart(2, '0');
    birthdateDayStr = birthdate
      .layBirthDayOfMonth()
      .toString()
      .padStart(2, '0');
    birthdateYearStr = birthdate.layBirthYear().toString();
    // Explicitly update validation state after initialization
    updateValidation();
  }
}

function updateValidation() {
  const result = parseDateInputValue(
    birthdateDayStr,
    birthdateMonthStr,
    birthdateYearStr
  );

  parsedDate = result.date;
  validationErrors = result.errors;
  isValid = !!parsedDate && validationErrors.length === 0;
}

// Initialize from provided birthdate (one-time) to prefill fields
$: if (birthdate && !birthdateYearStr) {
  initializeFromBirthdate();
}

// Update parsed date and validation state when inputs change
$: {
  // Register dependencies
  const _ = [birthdateDayStr, birthdateMonthStr, birthdateYearStr];
  updateValidation();
}

// Create Birthdate object and emit change event when valid
$: if (isValid && parsedDate) {
  const newBirthdate = Birthdate.FromYMD(
    parsedDate.getUTCFullYear(),
    parsedDate.getUTCMonth(),
    parsedDate.getUTCDate()
  );
  if (
    !birthdate ||
    birthdate.layBirthYear() !== newBirthdate.layBirthYear() ||
    birthdate.layBirthMonth() !== newBirthdate.layBirthMonth() ||
    birthdate.layBirthDayOfMonth() !== newBirthdate.layBirthDayOfMonth()
  ) {
    birthdate = newBirthdate;
    onchange?.({ birthdate: newBirthdate });
  }
}

function handleKeyUp(event, nextInputEl) {
  if (/^[a-zA-Z0-9]$/.test(event.key) && event.target.value.length === 2) {
    nextInputEl.focus();
  }
}

function autoFocusAction(el) {
  if (autoFocus) {
    el.focus();
  }
}
</script>

<div class="date-input-container">
  <div class="date-input-item">
    <label class="date-input-label" for="{inputId}-month">Month</label>
    <input
      class="input input-width-2"
      class:invalid={!isValid && birthdateMonthStr}
      id="{inputId}-month"
      name="{inputId}-month"
      type="text"
      inputmode="numeric"
      placeholder="MM"
      maxlength="2"
      on:keyup={(event) => handleKeyUp(event, dayInputEl)}
      bind:value={birthdateMonthStr}
      use:autoFocusAction
    />
  </div>
  <div class="date-input-item">
    <label class="date-input-label" for="{inputId}-day">Day</label>
    <input
      class="input input-width-2"
      class:invalid={!isValid && birthdateDayStr}
      id="{inputId}-day"
      name="{inputId}-day"
      type="text"
      inputmode="numeric"
      placeholder="DD"
      maxlength="2"
      on:keyup={(event) => handleKeyUp(event, yearInputEl)}
      bind:value={birthdateDayStr}
      bind:this={dayInputEl}
    />
  </div>
  <div class="date-input-item">
    <label class="date-input-label" for="{inputId}-year">Year</label>
    <input
      class="input input-width-4"
      class:invalid={!isValid && birthdateYearStr}
      id="{inputId}-year"
      name="{inputId}-year"
      type="text"
      inputmode="numeric"
      placeholder="YYYY"
      maxlength="4"
      bind:value={birthdateYearStr}
      bind:this={yearInputEl}
    />
  </div>
</div>

{#if validationErrors.length > 0 && (birthdateMonthStr || birthdateDayStr || birthdateYearStr)}
  <div class="error-messages">
    {#each validationErrors as error}
      <span class="error-message">{error}</span>
    {/each}
  </div>
{/if}

<style>
  .date-input-container {
    margin-bottom: 10px;
  }
  .date-input-item {
    display: inline-block;
    margin-right: 0.75rem;
  }
  .date-input-label {
    display: block;
    margin-bottom: 0.3rem;
    font-weight: 500;
    font-size: 0.85rem;
    color: #4b5563;
  }
  .input {
    font-size: 1rem;
    line-height: 1.4;
    padding: 0.65rem 0.75rem;
    border: 1.5px solid #d1d5db;
    border-radius: 6px;
    background-color: white;
    color: #0b0c0c;
    font-family: inherit;
    appearance: none;
    transition:
      border-color 0.15s ease,
      box-shadow 0.15s ease;
  }
  .input:focus {
    outline: none;
    border-color: #081d88;
    box-shadow: 0 0 0 3px rgba(8, 29, 136, 0.15);
  }
  .input-width-2 {
    max-width: 3.75rem;
  }
  .input-width-4 {
    max-width: 5.5rem;
  }

  .input.invalid {
    border-color: #d4351c;
    background-color: #fef5f5;
  }
  .input.invalid:focus {
    box-shadow: 0 0 0 3px rgba(212, 53, 28, 0.15);
  }

  .error-messages {
    margin-top: 0.5rem;
  }

  .error-message {
    display: block;
    color: #d4351c;
    font-size: 0.85rem;
    margin-bottom: 0.2rem;
  }
</style>
